import {Injectable} from '@angular/core';
import {DataService} from './data.service';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {Observable} from 'rxjs';
import {environment} from '../../environments/environment';
import {
    ActiveOrderResponse,
    AdminLoginRequest,
    AdminLoginResponse,
    AdminProfileUpdateRequest,
    AdminProfileUpdateResponse,
    AdminUserCreateRequest,
    AdminUserResponse,
    AdminUsersResponse,
    AdminUserUpdateRequest,
    AirportQueueResponse,
    ApiResponse,
    CarDetailsResponse,
    CarsOnMapResponse,
    CarsResponse,
    CompanySessionsResponse,
    DrinkSafeRequest,
    DriverResponse,
    MessageAllDriversRequest,
    MessageCarRequest,
    MessageCarsResponse,
    OrderLocationsResponse,
    OrdersPageResponse,
    OverviewResponse,
    PackageRequest,
    PaymentMethodOrdersReportResponse,
    PendingVoucher,
    PendingVouchersResponse,
    PosAppVersionResponse,
    PosTerminalResponse,
    PosTerminalsResponse,
    PosTerminalUpdateRequest,
    SuspendRequest,
    TotalOrdersReportResponse,
    UnsuspendRequest
} from '../models/api.models';

@Injectable()
export class RestService {
    baseURL: string;
    httpOptions = {
        headers: new HttpHeaders({'Content-Type': 'application/json'})
    };

    constructor(
        private httpClient: HttpClient,
        private dataService: DataService) {
        this.baseURL = `${environment.baseUrl}/soc-admin/v1/`;
    }

    login(userCredentials: AdminLoginRequest): Observable<AdminLoginResponse> {
        const callUrl = this.baseURL + 'login';
        const body = JSON.stringify(userCredentials);
        return this.httpClient.post<AdminLoginResponse>(callUrl, body, this.httpOptions);
    }

    logout(): Observable<ApiResponse> {
        const callUrl = this.baseURL + 'logout';
        const headers = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Token', this.dataService.getSessionIdentifier());
        return this.httpClient.get<ApiResponse>(callUrl, {headers});
    }

    checkSession(sessionId: string): Observable<AdminLoginResponse> {
        const callUrl = this.baseURL + 'checksession';
        const headers = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Token', sessionId);
        return this.httpClient.get<AdminLoginResponse>(callUrl, {headers});
    }

    updateAdminProfile(profile: AdminProfileUpdateRequest): Observable<AdminProfileUpdateResponse> {
        const callUrl = this.baseURL + 'profile';
        const headers = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Token', this.dataService.getSessionIdentifier());
        return this.httpClient.post<AdminProfileUpdateResponse>(callUrl, JSON.stringify(profile), {headers});
    }

    getAdminUsers(): Observable<AdminUsersResponse> {
        const callUrl = this.baseURL + 'users';
        const headers = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Token', this.dataService.getSessionIdentifier());
        return this.httpClient.get<AdminUsersResponse>(callUrl, {headers});
    }

    createAdminUser(request: AdminUserCreateRequest): Observable<AdminUserResponse> {
        const callUrl = this.baseURL + 'users';
        const headers = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Token', this.dataService.getSessionIdentifier());
        return this.httpClient.post<AdminUserResponse>(callUrl, JSON.stringify(request), {headers});
    }

    updateAdminUser(request: AdminUserUpdateRequest): Observable<AdminUserResponse> {
        const callUrl = this.baseURL + 'users/update';
        const headers = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Token', this.dataService.getSessionIdentifier());
        return this.httpClient.post<AdminUserResponse>(callUrl, JSON.stringify(request), {headers});
    }

    deleteAdminUser(username: string): Observable<ApiResponse> {
        const callUrl = this.baseURL + 'users/' + encodeURIComponent(username);
        const headers = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Token', this.dataService.getSessionIdentifier());
        return this.httpClient.delete<ApiResponse>(callUrl, {headers});
    }

    resetAdminUserPassword(username: string): Observable<AdminUserResponse> {
        const callUrl = this.baseURL + 'users/' + encodeURIComponent(username) + '/reset-password';
        const headers = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Token', this.dataService.getSessionIdentifier());
        return this.httpClient.post<AdminUserResponse>(callUrl, {}, {headers});
    }

    messageAllDrivers(message: MessageAllDriversRequest): Observable<ApiResponse> {
        const callUrl = this.baseURL + 'messages/all-drivers';
        const headers = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Token', this.dataService.getSessionIdentifier());
        return this.httpClient.post<ApiResponse>(callUrl, JSON.stringify(message), {headers});
    }

    messageCar(message: MessageCarRequest): Observable<ApiResponse> {
        const callUrl = this.baseURL + 'messages/car';
        const headers = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Token', this.dataService.getSessionIdentifier());
        return this.httpClient.post<ApiResponse>(callUrl, JSON.stringify(message), {headers});
    }

    getMessageCars(): Observable<MessageCarsResponse> {
        const callUrl = this.baseURL + 'messages/cars';
        const headers = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Token', this.dataService.getSessionIdentifier());
        return this.httpClient.get<MessageCarsResponse>(callUrl, {headers});
    }

    getOverviewData(): Observable<OverviewResponse> {
        const callUrl = this.baseURL + 'overview';
        const headers = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Token', this.dataService.getSessionIdentifier());
        return this.httpClient.get<OverviewResponse>(callUrl, {headers});
    }

    getOverviewOrdersByPlatform(): Observable<TotalOrdersReportResponse> {
        const callUrl = this.baseURL + 'overview/orders-by-platform';
        const headers = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Token', this.dataService.getSessionIdentifier());
        return this.httpClient.get<TotalOrdersReportResponse>(callUrl, {headers});
    }

    getOverviewOrdersByPaymentMethod(): Observable<PaymentMethodOrdersReportResponse> {
        const callUrl = this.baseURL + 'overview/orders-by-payment-method';
        const headers = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Token', this.dataService.getSessionIdentifier());
        return this.httpClient.get<PaymentMethodOrdersReportResponse>(callUrl, {headers});
    }

    getCars(): Observable<CarsResponse> {
        const callUrl = this.baseURL + 'cars';
        const headers = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Token', this.dataService.getSessionIdentifier());
        return this.httpClient.get<CarsResponse>(callUrl, {headers});
    }

    getExternalCars(): Observable<CarsResponse> {
        const callUrl = this.baseURL + 'cars-external';
        const headers = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Token', this.dataService.getSessionIdentifier());
        return this.httpClient.get<CarsResponse>(callUrl, {headers});
    }

    getPendingVouchers(page: number, pageSize: number, statuses: string[], search: string,
                       car: string, client: string, sort: string, direction: string, includeOptions: boolean): Observable<PendingVouchersResponse> {
        const callUrl = this.baseURL + 'pendingvouchers?page=' + page + '&pageSize=' + pageSize
            + '&statuses=' + encodeURIComponent(statuses.length ? statuses.join(',') : 'none')
            + '&search=' + encodeURIComponent(search) + '&car=' + encodeURIComponent(car)
            + '&client=' + encodeURIComponent(client) + '&sort=' + encodeURIComponent(sort)
            + '&direction=' + encodeURIComponent(direction) + '&includeOptions=' + includeOptions;
        const headers = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Token', this.dataService.getSessionIdentifier());
        return this.httpClient.get<PendingVouchersResponse>(callUrl, {headers});
    }

    getOrders(page: number, pageSize: number, phoneNumber: string, car: string, status: string, source: string,
              paymentMethod: string, exactCar = false, driverIdentifier = ''): Observable<OrdersPageResponse> {
        let callUrl = this.baseURL + 'getorders?page=' + page + '&pageSize=' + pageSize;
        if (phoneNumber !== '') {
            callUrl += '&phoneNumber=' + encodeURIComponent(phoneNumber);
        }
        if (car !== '') {
            callUrl += '&car=' + encodeURIComponent(car);
        }
        if (status !== '') {
            callUrl += '&status=' + encodeURIComponent(status);
        }
        if (source !== '') {
            callUrl += '&source=' + encodeURIComponent(source);
        }
        if (paymentMethod !== '') {
            callUrl += '&paymentMethod=' + encodeURIComponent(paymentMethod);
        }
        if (exactCar) {
            callUrl += '&exactCar=true';
        }
        if (driverIdentifier !== '') {
            callUrl += '&driverIdentifier=' + encodeURIComponent(driverIdentifier);
        }
        const headers = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Token', this.dataService.getSessionIdentifier());
        return this.httpClient.get<OrdersPageResponse>(callUrl, {headers});
    }

    getOrderLocations(orderId: string): Observable<OrderLocationsResponse> {
        const callUrl = this.baseURL + 'orderLocations/' + encodeURIComponent(orderId);
        const headers = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Token', this.dataService.getSessionIdentifier());
        return this.httpClient.get<OrderLocationsResponse>(callUrl, {headers});
    }

    getActiveOrder(carIdentifier: string): Observable<ActiveOrderResponse> {
        const callUrl = this.baseURL + 'activeOrder/' + encodeURIComponent(carIdentifier);
        const headers = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Token', this.dataService.getSessionIdentifier());
        return this.httpClient.get<ActiveOrderResponse>(callUrl, {headers});
    }

    getPosTerminals(): Observable<PosTerminalsResponse> {
        const callUrl = this.baseURL + 'posterminals';
        const headers = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Token', this.dataService.getSessionIdentifier());
        return this.httpClient.get<PosTerminalsResponse>(callUrl, {headers});
    }

    getPosAppVersion(deviceId: string): Observable<PosAppVersionResponse> {
        const callUrl = this.baseURL + 'posappversion/' + encodeURIComponent(deviceId);
        const headers = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Token', this.dataService.getSessionIdentifier());
        return this.httpClient.get<PosAppVersionResponse>(callUrl, {headers});
    }

    updatePosAppVersion(deviceId: string, posAppVersion: string): Observable<PosAppVersionResponse> {
        const callUrl = this.baseURL + 'updateposappversion';
        const headers = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Token', this.dataService.getSessionIdentifier());
        return this.httpClient.post<PosAppVersionResponse>(callUrl, JSON.stringify({
            DeviceId: deviceId,
            PosAppVer: posAppVersion
        }), {headers});
    }

    updatePosTerminal(dataToSend: PosTerminalUpdateRequest): Observable<PosTerminalResponse> {
        const callUrl = this.baseURL + 'updateposterminal';
        const headers = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Token', this.dataService.getSessionIdentifier());
        return this.httpClient.post<PosTerminalResponse>(callUrl, JSON.stringify(dataToSend), {headers});
    }

    resetPosPairingCode(idTerminal: string): Observable<PosTerminalResponse> {
        const callUrl = this.baseURL + 'resetpospairingcode';
        const headers = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Token', this.dataService.getSessionIdentifier());
        return this.httpClient.post<PosTerminalResponse>(callUrl, JSON.stringify({IdTerminal: idTerminal}), {headers});
    }

    payPendingVoucher(dataToSend: PendingVoucher): Observable<ApiResponse> {
        const callUrl = this.baseURL + 'pendingvoucherpay';
        const body = JSON.stringify(dataToSend);
        const headers = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Token', this.dataService.getSessionIdentifier());
        return this.httpClient.post<ApiResponse>(callUrl, body, {headers});
    }

    deletePendingVoucher(voucherId: string): Observable<ApiResponse> {
        const callUrl = this.baseURL + 'pendingvouchers/' + encodeURIComponent(voucherId);
        const headers = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Token', this.dataService.getSessionIdentifier());
        return this.httpClient.delete<ApiResponse>(callUrl, {headers});
    }

    getAirportCars(): Observable<AirportQueueResponse> {
        const callUrl = this.baseURL + 'airportcars';
        const headers = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Token', this.dataService.getSessionIdentifier());
        return this.httpClient.get<AirportQueueResponse>(callUrl, {headers});
    }

    getOnlineCars(): Observable<CompanySessionsResponse> {
        const callUrl = this.baseURL + 'onlinecars';
        const headers = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Token', this.dataService.getSessionIdentifier());
        return this.httpClient.get<CompanySessionsResponse>(callUrl, {headers});
    }

    getCarDetails(carIdentifier: string): Observable<CarDetailsResponse> {
        const callUrl = this.baseURL + 'cardetails/' + carIdentifier;
        const headers = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Token', this.dataService.getSessionIdentifier());
        return this.httpClient.get<CarDetailsResponse>(callUrl, {headers});
    }

    getExternalCarDetails(carIdentifier: string): Observable<CarDetailsResponse> {
        const callUrl = this.baseURL + 'cars-external/' + encodeURIComponent(carIdentifier);
        const headers = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Token', this.dataService.getSessionIdentifier());
        return this.httpClient.get<CarDetailsResponse>(callUrl, {headers});
    }

    getDriverHistory(driverId: string, carIdentifier = '', external = false): Observable<DriverResponse> {
        let callUrl = this.baseURL + 'driverhistory/' + encodeURIComponent(driverId);
        if (carIdentifier !== '') {
            callUrl += '?carIdentifier=' + encodeURIComponent(carIdentifier) + '&external=' + external;
        }
        const headers = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Token', this.dataService.getSessionIdentifier());
        return this.httpClient.get<DriverResponse>(callUrl, {headers});
    }

    unpairDevice(carIdentifier: string): Observable<ApiResponse> {
        const callUrl = this.baseURL + 'unpairdevice/' + carIdentifier;
        const headers = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Token', this.dataService.getSessionIdentifier());
        return this.httpClient.get<ApiResponse>(callUrl, {headers});
    }

    unpairExternalDevice(carIdentifier: string): Observable<ApiResponse> {
        const callUrl = this.baseURL + 'cars-external/' + encodeURIComponent(carIdentifier) + '/unpair';
        const headers = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Token', this.dataService.getSessionIdentifier());
        return this.httpClient.get<ApiResponse>(callUrl, {headers});
    }

    getCarsOnMap(): Observable<CarsOnMapResponse> {
        const callUrl = this.baseURL + 'carsonmap';
        const headers = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Token', this.dataService.getSessionIdentifier());
        return this.httpClient.get<CarsOnMapResponse>(callUrl, {headers});
    }

    updateDrinkSafe(dataToSend: DrinkSafeRequest): Observable<ApiResponse> {
        const callUrl = this.baseURL + 'set-drink-safe';
        const body = JSON.stringify(dataToSend);
        const headers = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Token', this.dataService.getSessionIdentifier());
        return this.httpClient.post<ApiResponse>(callUrl, body, {headers});
    }

    updateExternalDrinkSafe(dataToSend: DrinkSafeRequest): Observable<ApiResponse> {
        const callUrl = this.baseURL + 'cars-external/set-drink-safe';
        const body = JSON.stringify(dataToSend);
        const headers = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Token', this.dataService.getSessionIdentifier());
        return this.httpClient.post<ApiResponse>(callUrl, body, {headers});
    }

    updatePackage(dataToSend: PackageRequest): Observable<ApiResponse> {
        const callUrl = this.baseURL + 'set-package';
        const body = JSON.stringify(dataToSend);
        const headers = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Token', this.dataService.getSessionIdentifier());
        return this.httpClient.post<ApiResponse>(callUrl, body, {headers});
    }

    updateExternalPackage(dataToSend: PackageRequest): Observable<ApiResponse> {
        const callUrl = this.baseURL + 'cars-external/set-package';
        const body = JSON.stringify(dataToSend);
        const headers = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Token', this.dataService.getSessionIdentifier());
        return this.httpClient.post<ApiResponse>(callUrl, body, {headers});
    }

    suspendDriver(dataToSend: SuspendRequest): Observable<ApiResponse> {
        const callUrl = this.baseURL + 'admin_suspend';
        const suspendBody = JSON.stringify(dataToSend);
        const headers = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Token', this.dataService.getSessionIdentifier());
        return this.httpClient.post<ApiResponse>(callUrl, suspendBody, {headers});
    }

    unsuspendDriver(dataToSend: UnsuspendRequest): Observable<ApiResponse> {
        const callUrl = this.baseURL + 'admin_unsuspend';
        const suspendBody = JSON.stringify(dataToSend);
        const headers = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Token', this.dataService.getSessionIdentifier());
        return this.httpClient.post<ApiResponse>(callUrl, suspendBody, {headers});
    }

}
